import { EditorProps as TipTapEditorProps } from "@tiptap/pm/view";
import { EditorContext, useEditor } from "@tiptap/react";
import * as React from "react";
import { useCollab } from "../contexts/collab-context";
import type { User } from "../contexts/user-context";
import { extensions } from "./extensions";
import "./tiptap-node/blockquote-node/blockquote-node.scss";
import "./tiptap-node/code-block-node/code-block-node.scss";
import "./tiptap-node/heading-node/heading-node.scss";
import "./tiptap-node/horizontal-rule-node/horizontal-rule-node.scss";
import "./tiptap-node/image-node/image-node.scss";
import "./tiptap-node/list-node/list-node.scss";
import "./tiptap-node/paragraph-node/paragraph-node.scss";
import { EditorProps } from "./types";

type EditorProviderProps = EditorProps & {
  user: User;
  children: React.ReactNode;
  editorProps: TipTapEditorProps;
  placeholderEmptyNodeClass?: string;
};

export function EditorProvider(props: EditorProviderProps) {
  const { placeholder = "Start writing...", user, onUpdate } = props;

  const { provider, ydoc } = useCollab();

  const editor = useEditor(
    {
      immediatelyRender: false,
      shouldRerenderOnTransaction: false,
      editorProps: props.editorProps,
      autofocus: true,
      extensions: extensions({
        provider,
        ydoc,
        user: { name: user.firstName ?? "Anonymous User", color: user.color },
        placeholder,
        placeholderEmptyNodeClass: props.placeholderEmptyNodeClass,
      }),
      onUpdate: onUpdate,
      onContentError({ editor, disableCollaboration }) {
        // Removes the collaboration extension.
        disableCollaboration();

        // Since the content is invalid, we don't want to emit an update
        // Preventing synchronization with other editors or to a server
        const emitUpdate = false;

        // Disable the editor to prevent further user input
        editor.setEditable(false, emitUpdate);

        // Maybe show a notification to the user that they need to refresh the app
        // TODO
      },
    },
    [provider, props.contentId],
  );

  const providerValue = React.useMemo(() => ({ editor }), [editor]);

  return (
    <EditorContext.Provider value={providerValue}>
      {props.children}
    </EditorContext.Provider>
  );
}

export function useEditorContext() {
  const editor = React.useContext(EditorContext);

  if (!editor) {
    throw new Error("Editor not found");
  }

  return editor;
}
